import React, { useState } from "react";
import { toast } from "sonner";
import { FaSave, FaTimes } from "react-icons/fa";

// The one shape + editor for a practice question, shared by every surface that
// writes one: the lesson builder ("Write a question by hand" and the inline edit
// on drafts/approved) and the question bank page.
//
// It exists because a question is edited in three places now and each had (or
// would have grown) its own copy of "four options, a correct letter, and the
// theory variant that has neither" — which is exactly the sort of thing that
// drifts until one screen quietly saves an invalid row.

export const EMPTY_QUESTION = {
    question_type: "mcq",
    level: "Medium",
    question: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correctAnswer: "A",
    explanation: "",
    segment_id: "",
};

const LETTERS = ["A", "B", "C", "D"];

// A quiz_bank row (as the API returns it) -> form state. Selects want strings,
// and a NULL segment_id has to become "" so it matches the "Whole lesson"
// option rather than leaving the select unset.
export const questionToForm = (q = {}) => ({
    question_type: q.question_type === "theory" ? "theory" : "mcq",
    level: ["Easy", "Medium", "Hard"].includes(q.level) ? q.level : "Medium",
    question: q.question || "",
    option_a: q.option_a || "",
    option_b: q.option_b || "",
    option_c: q.option_c || "",
    option_d: q.option_d || "",
    correctAnswer: LETTERS.includes(q.correctAnswer) ? q.correctAnswer : "A",
    explanation: q.explanation || "",
    segment_id: q.segment_id ? String(q.segment_id) : "",
    topic: q.topic || "",
});

// Returns the message to toast, or null when the form is good. Mirrors the
// server-side checks in TeacherQuestionController/LessonController so a teacher
// is told what's wrong before the round trip.
export const validateQuestion = (form) => {
    if (!form.question.trim()) return "Write the question text";
    if (form.question_type === "mcq") {
        if (!LETTERS.every((l) => (form[`option_${l.toLowerCase()}`] || "").trim())) {
            return "A multiple-choice question needs all four options";
        }
        if (!LETTERS.includes(form.correctAnswer)) return "Choose which option (A–D) is correct";
    }
    if (form.topic !== undefined && !String(form.topic).trim()) return "Choose a topic";
    return null;
};

// The fields themselves. `segments` adds the sub-topic picker (the lesson
// builder passes its lesson's sub-topics; the question bank leaves it out and
// slots a topic picker in through `children` instead). `children` may be a
// render prop — it's given (form, set) so a caller's own field can read and
// write the same state as the built-in ones.
export const QuestionFields = ({ form, set, segments = null, children = null }) => {
    const isMcq = form.question_type === "mcq";

    return (
        <>
            <div className={`grid grid-cols-1 gap-3 ${segments ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                {segments && (
                    <label className="text-xs font-medium text-gray-600">Sub-topic
                        <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm" value={form.segment_id}
                            onChange={(e) => set({ segment_id: e.target.value })}>
                            <option value="">Whole lesson</option>
                            {segments.map((s, i) => <option key={s.id} value={s.id}>{i + 1}. {s.title}</option>)}
                        </select>
                    </label>
                )}
                <label className="text-xs font-medium text-gray-600">Type
                    <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm" value={form.question_type}
                        onChange={(e) => set({ question_type: e.target.value })}>
                        <option value="mcq">Multiple choice</option>
                        <option value="theory">Theory</option>
                    </select>
                </label>
                <label className="text-xs font-medium text-gray-600">Difficulty
                    <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 text-sm" value={form.level}
                        onChange={(e) => set({ level: e.target.value })}>
                        <option>Easy</option><option>Medium</option><option>Hard</option>
                    </select>
                </label>
            </div>

            {typeof children === "function" ? children(form, set) : children}

            <textarea placeholder="Write the question in plain text (avoid $ or LaTeX — e.g. x^2 not $x^2$)" rows={3}
                className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                value={form.question} onChange={(e) => set({ question: e.target.value })} />

            {isMcq && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {LETTERS.map((l) => (
                            <input key={l} placeholder={`Option ${l}`} className="border border-gray-200 rounded-lg p-2 text-sm"
                                value={form[`option_${l.toLowerCase()}`]}
                                onChange={(e) => set({ [`option_${l.toLowerCase()}`]: e.target.value })} />
                        ))}
                    </div>
                    <label className="text-xs font-medium text-gray-600 block">Correct answer
                        <select className="border border-gray-200 rounded-lg p-2 w-full mt-1 sm:w-40 text-sm" value={form.correctAnswer}
                            onChange={(e) => set({ correctAnswer: e.target.value })}>
                            {LETTERS.map((l) => <option key={l}>{l}</option>)}
                        </select>
                    </label>
                </>
            )}

            <textarea placeholder={isMcq ? "Explanation (optional)" : "Model answer (optional)"} rows={2}
                className="border border-gray-200 rounded-lg p-2 w-full text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                value={form.explanation} onChange={(e) => set({ explanation: e.target.value })} />
        </>
    );
};

// Editing an EXISTING question, in place of the row it replaces. `onSave` gets
// the form and may throw — the message is surfaced by the caller's toast, and
// the editor stays open with the teacher's edits intact.
export const QuestionEditor = ({ question, segments = null, onSave, onCancel, children = null }) => {
    const [form, setForm] = useState(() => questionToForm(question));
    const [saving, setSaving] = useState(false);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async () => {
        const problem = validateQuestion(form);
        if (problem) { toast.error(problem); return; }
        setSaving(true);
        try {
            // Only send segment_id when this editor actually offered the picker.
            // A caller without one (the question bank, which has no single
            // lesson in view) would otherwise echo the field back and unfile the
            // question from the sub-topic it was generated from.
            const { segment_id, ...withoutSegment } = form;
            await onSave(segments ? form : withoutSegment);
        } catch (err) {
            // Stay open with the teacher's edits intact so they can fix and retry.
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-xl border-2 border-blue-300 bg-blue-50/20 p-3 sm:p-4 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">Edit question</h4>
                <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600"><FaTimes size={13} /></button>
            </div>

            <QuestionFields form={form} set={set} segments={segments}>{children}</QuestionFields>

            <div className="flex items-center gap-3 pt-1">
                <button type="button" onClick={submit} disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                    <FaSave size={12} /> {saving ? "Saving…" : "Save changes"}
                </button>
                <button type="button" onClick={onCancel} className="text-gray-500 text-sm font-semibold">Cancel</button>
            </div>
        </div>
    );
};
